/**
 * RoTabPane - 标签页面板组件
 * 
 * 属性:
 * - label: 标签显示的文字
 * - name: 标签的唯一标识，对应 ro-tabs 的 value
 * - disabled: 是否禁用
 * - lazy: 是否延迟渲染内容
 */
class RoTabPane extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this._loaded = false;
    this._active = false;
  }

  static get observedAttributes() {
    return ["label", "name", "disabled", "lazy"];
  }

  connectedCallback() {
    this._render();
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (oldVal === newVal) return;
    if (name === "label" || name === "name") {
      // 通知父组件更新头部
      const parent = this.closest("ro-tabs");
      if (parent && typeof parent._updateTabs === "function") {
        parent._updateTabs();
      }
    }
  }

  _render() {
    const isLazy = this.hasAttribute("lazy");
    const shouldRender = !isLazy || this._loaded || this._active;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
        }
        :host(:not([active])),
        :host([hidden]) {
          display: none;
        }
      </style>
      <div class="pane-content" part="pane-content">
        ${shouldRender ? '<slot></slot>' : ''}
      </div>
    `;

    if (shouldRender) {
      this._loaded = true;
    }
  }

  get active() {
    return this._active;
  }

  set active(val) {
    this._active = !!val;
    if (this._active) {
      this.setAttribute("active", "");
      if (!this._loaded) {
        this._render(); // 激活时渲染内容（支持 lazy）
      }
    } else {
      this.removeAttribute("active");
    }
  }

  get label() {
    return this.getAttribute("label") || "";
  }

  get name() {
    return this.getAttribute("name") || "";
  }

  get disabled() {
    return this.hasAttribute("disabled");
  }
}

customElements.define("ro-tab-pane", RoTabPane);

/**
 * RoTabs - 标签页组件
 * 
 * 属性:
 * - value / active-name: 当前选中的标签 name
 * - type: 风格，capsule (胶囊，默认) | tab (下划线)
 * - size: 尺寸，L | M | S (默认 L)
 * - align: 对齐方式，left | center | right (默认 center)
 * - scroll-into: 滚动定位，left | center | right (默认 center)
 * - background: 整体背景色，默认 none
 * - tab-background: 标签块背景色 (capsule 模式下有效)
 * - active-background: 选中项背景色
 * - active-color: 选中项文字颜色
 * - border-radius: 整体圆角，默认 90px
 * - no-tab-header: 布尔属性，存在时隐藏 Tab 头，仅展示内容区（如单 Tab 页面）
 *
 * ro-tab-pane 带 hidden 属性时不会参与 Tab 头渲染（DOM 可保留，供接口动态显隐）。
 *
 * 插槽:
 * - 默认插槽: 放置 ro-tab-pane
 * - icon插槽: 在 ro-tab-pane 中使用 slot="icon"
 */
class RoTabs extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this._panes = [];
    this._value = "";
    this._mo = null;
    this._render();
  }

  static get observedAttributes() {
    return [
      "value",
      "active-name",
      "type",
      "size",
      "align",
      "scroll-into",
      "background",
      "tab-background",
      "active-background",
      "active-color",
      "border-radius",
      "gap",
      "no-tab-header",
    ];
  }

  _render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: flex;
          flex-direction: column;
          width: 100%;
          --ro-tabs-bg: none;
          --ro-tabs-header-bg: rgba(246, 246, 246, 0.8);
          --ro-tabs-active-bg: var(--black, #161616);
          --ro-tabs-active-color: var(--white, #fff);
          --ro-tabs-text-color: var(--black, #161616);
          --ro-tabs-radius: 90px;
          --ro-tabs-item-radius: 41px;
          --ro-tabs-header-padding: 4px;
          --ro-tabs-item-padding: 10px 24px;
          --ro-tabs-gap: 8px;
          --ro-tabs-font-size: 14px;
          --ro-tabs-height: 48px;
        }

        .ro-tabs {
          display: flex;
          flex-direction: column;
          width: 100%;
          background: var(--ro-tabs-bg);
        }

        .ro-tabs-header-wrapper {
          display: flex;
          width: 100%;
          max-width: 100%;
          overflow: hidden;
          position: relative;
          box-sizing: border-box;
          flex-shrink: 1;
          min-width: 0;
          justify-content: center;
        }

        :host([align="left"]) .ro-tabs-header-wrapper { justify-content: flex-start; }
        :host([align="center"]) .ro-tabs-header-wrapper { justify-content: center; }
        :host([align="right"]) .ro-tabs-header-wrapper { justify-content: flex-end; }
        :host(:not([align])) .ro-tabs-header-wrapper { justify-content: center; }

        .ro-tabs-header {
          display: flex;
          align-items: center;
          padding: var(--ro-tabs-header-padding);
          background: var(--ro-tabs-header-bg);
          border-radius: var(--ro-tabs-radius);
          backdrop-filter: blur(15px);
          -webkit-backdrop-filter: blur(15px);
          gap: var(--ro-tabs-gap);
          overflow-x: auto;
          scrollbar-width: none; /* Firefox */
          transition: all 0.3s ease;
          flex: 0 1 auto;
          max-width: 100%;
          box-sizing: border-box;
          height: var(--ro-tabs-height);
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
          scroll-padding-inline: 24px;
        }

        .ro-tabs-header::-webkit-scrollbar {
          display: none; /* Chrome/Safari */
        }

        .ro-tab-item {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: var(--ro-tabs-item-padding);
          border-radius: var(--ro-tabs-item-radius);
          cursor: pointer;
          white-space: nowrap;
          font-size: var(--ro-tabs-font-size);
          line-height: 20px;
          color: var(--ro-tabs-text-color);
          transition: all 0.3s ease;
          position: relative;
          user-select: none;
          flex-shrink: 0;
          box-sizing: border-box;
          height: calc(var(--ro-tabs-height) - var(--ro-tabs-header-padding) * 2);
        }

        .ro-tab-item.active {
          background: var(--ro-tabs-active-bg);
          color: var(--ro-tabs-active-color);
          font-weight: 500;
        }

        .ro-tab-item.disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .ro-tab-item-icon {
          display: flex;
          align-items: center;
          margin-right: 8px;
        }

        /* 下划线模式 */
        :host([type="tab"]) .ro-tabs-header {
          background: none;
          backdrop-filter: none;
          border-radius: 0;
          padding: 0;
          gap: 40px;
          height: auto;
        }
        :host([type="tab"]) .ro-tab-item {
          border-radius: 0;
          padding: 16px 0;
          color: var(--gray, #6c6d6e);
          height: 48px;
        }
        :host([type="tab"]) .ro-tab-item.active {
          background: none;
          color: var(--black, #161616);
          border-bottom: 2px solid var(--black, #161616);
          font-weight: 600;
        }

        /* 尺寸 L (默认) */
        :host([size="L"]), :host(:not([size])) {
          --ro-tabs-height: 48px;
          --ro-tabs-font-size: 14px;
          --ro-tabs-item-padding: 10px 24px;
          --ro-tabs-gap: 8px;
        }
        /* 尺寸 M */
        :host([size="M"]) {
          --ro-tabs-height: 40px;
          --ro-tabs-font-size: 14px;
          --ro-tabs-item-padding: 8px 20px;
          --ro-tabs-gap: 4px;
          --ro-tabs-item-radius: 30px;
        }
        /* 尺寸 S */
        :host([size="S"]) {
          --ro-tabs-height: 32px;
          --ro-tabs-font-size: 12px;
          --ro-tabs-item-padding: 6px 16px;
          --ro-tabs-gap: 4px;
          --ro-tabs-item-radius: 24px;
        }

        .ro-tabs-content {
          /* margin-top: 16px; */
          width: 100%;
        }

        /* 隐藏 Tab 头（如 Find Store 仅一个 tab 且 showTab 为 false） */
        :host([no-tab-header]) .ro-tabs-header-wrapper {
          display: none;
        }
      </style>
      <div class="ro-tabs" part="container">
        <div class="ro-tabs-header-wrapper" part="header-wrapper">
          <div class="ro-tabs-header" part="header" role="tablist"></div>
        </div>
        <div class="ro-tabs-content" part="content">
          <slot></slot>
        </div>
      </div>
    `;

    this._headerEl = this.shadowRoot.querySelector(".ro-tabs-header");
  }

  connectedCallback() {
    this._updateTabs();
    this._initMutationObserver();
    this._syncValue();
  }

  disconnectedCallback() {
    if (this._mo) this._mo.disconnect();
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (oldVal === newVal) return;
    if (name === "value" || name === "active-name") {
      this._value = newVal;
      this._updateActivePane();
    } else {
      this._updateStyles();
    }
  }

  _initMutationObserver() {
    this._mo = new MutationObserver(() => {
      this._updateTabs();
    });
    this._mo.observe(this, { childList: true });
  }

  _updateTabs() {
    const panes = Array.from(this.children).filter(
      (child) => child.tagName.toLowerCase() === 'ro-tab-pane' && !child.hasAttribute('hidden')
    );
    this._panes = panes;
    
    this._headerEl.innerHTML = "";
    panes.forEach((pane, index) => {
      const name = pane.getAttribute("name") || String(index);
      const label = pane.getAttribute("label") || "";
      const disabled = pane.hasAttribute("disabled");
      
      const tabItem = document.createElement("div");
      tabItem.className = `ro-tab-item ${name === this._value ? 'active' : ''} ${disabled ? 'disabled' : ''}`;
      tabItem.setAttribute("role", "tab");
      tabItem.setAttribute("aria-selected", name === this._value ? "true" : "false");
      tabItem.dataset.name = name;
      tabItem.part = `tab-item ${name === this._value ? 'tab-item-active' : ''}`;
      
      // 处理 icon 插槽
      const iconSlot = pane.querySelector('[slot="icon"]');
      if (iconSlot) {
        const iconWrap = document.createElement("span");
        iconWrap.className = "ro-tab-item-icon";
        const clone = iconSlot.cloneNode(true);
        iconWrap.appendChild(clone);
        tabItem.appendChild(iconWrap);
      }
      
      const labelText = document.createElement("span");
      labelText.textContent = label;
      tabItem.appendChild(labelText);
      
      tabItem.addEventListener("click", () => {
        if (!disabled) {
          this.value = name;
        }
      });
      
      this._headerEl.appendChild(tabItem);
    });

    this._updateActivePane();
  }

  _updateActivePane() {
    // 遍历全部 pane（含 hidden）：hidden 或未选中须取消 active，避免内容区仍占位展示
    const allPanes = Array.from(this.children).filter(
      (child) => child.tagName.toLowerCase() === "ro-tab-pane"
    );
    allPanes.forEach((pane, index) => {
      const name = pane.getAttribute("name") || String(index);
      const isActive = !pane.hasAttribute("hidden") && name === this._value;
      pane.active = isActive;
    });

    const items = this._headerEl.querySelectorAll(".ro-tab-item");
    items.forEach(item => {
      const isActive = item.dataset.name === this._value;
      item.classList.toggle("active", isActive);
      item.setAttribute("aria-selected", isActive ? "true" : "false");
      item.part = `tab-item ${isActive ? 'tab-item-active' : ''}`;
    });

    this._scrollActiveTabIntoView();
  }

  _scrollActiveTabIntoView() {
    const activeItem = this._headerEl.querySelector(".ro-tab-item.active");
    if (!activeItem) return;

    let scrollInto = this.getAttribute("scroll-into") || "center";
    let inline = "center";
    if (scrollInto === "left") inline = "start";
    if (scrollInto === "right") inline = "end";
    
    // 确保在渲染完成后执行滚动
    setTimeout(() => {
      activeItem.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: inline
      });
    }, 100);
  }

  _syncValue() {
    const val = this.getAttribute("value") || this.getAttribute("active-name");
    if (val) {
      this._value = val;
    } else if (this._panes.length > 0) {
      // 默认选中第一个非禁用的
      const firstEnabled = this._panes.find(p => !p.disabled);
      if (firstEnabled) {
        this._value = firstEnabled.getAttribute("name") || "0";
      }
    }
    this._updateActivePane();
  }

  _updateStyles() {
    const bg = this.getAttribute("background");
    const tabBg = this.getAttribute("tab-background");
    const activeBg = this.getAttribute("active-background");
    const activeColor = this.getAttribute("active-color");
    const radius = this.getAttribute("border-radius");
    const gap = this.getAttribute("gap");

    if (bg) this.style.setProperty("--ro-tabs-bg", bg);
    if (tabBg) this.style.setProperty("--ro-tabs-header-bg", tabBg);
    if (activeBg) this.style.setProperty("--ro-tabs-active-bg", activeBg);
    if (activeColor) this.style.setProperty("--ro-tabs-active-color", activeColor);
    if (radius) this.style.setProperty("--ro-tabs-radius", radius);
    if (gap) this.style.setProperty("--ro-tabs-gap", gap.includes('px') ? gap : `${gap}px`);
  }

  get value() {
    return this._value;
  }

  set value(val) {
    if (this._value === val) return;
    this._value = val;
    this.setAttribute("value", val);
    this._updateActivePane();
    this.dispatchEvent(new CustomEvent("change", {
      detail: { value: val },
      bubbles: true,
      composed: true
    }));
  }
}

customElements.define("ro-tabs", RoTabs);
